/*
 * <cart-discount> — applies and removes discount codes from the cart drawer.
 *
 * Uses the Cart Ajax API's discount support on /cart/update.js, which stores the
 * codes on the cart itself, so totals update in the drawer and carry through to
 * checkout. Codes are sent as one comma-separated list — that list IS the cart's
 * discounts, so removing a code means re-sending the list without it, and an
 * empty string clears them all.
 *
 * Shopify accepts the request even when a code cannot be applied, so a submit
 * only counts as a success when the code comes back in the cart's discount
 * applications. When it doesn't, the previous list is restored so a rejected
 * code is never left on the cart.
 */
const CART_DISCOUNT_SECTIONS = [
  { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
  { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
];

class CartDiscount extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-discount-form]');
    this.input = this.querySelector('[data-discount-input]');
    this.errorElement = this.querySelector('[data-discount-error]');

    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.clearError = this.clearError.bind(this);

    this.form?.addEventListener('submit', this.handleSubmit);
    this.input?.addEventListener('input', this.clearError);
    this.addEventListener('click', this.handleClick);
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.handleSubmit);
    this.input?.removeEventListener('input', this.clearError);
    this.removeEventListener('click', this.handleClick);
  }

  /* Codes currently on the cart, read back off the Liquid-rendered chips. */
  get appliedCodes() {
    return [...this.querySelectorAll('[data-discount-code]')].map((chip) => chip.dataset.discountCode);
  }

  handleSubmit(event) {
    event.preventDefault();
    if (this.classList.contains('is-loading')) return;

    const code = this.input?.value.trim();
    if (!code) return;

    const applied = this.appliedCodes;
    if (applied.some((existing) => existing.toLowerCase() === code.toLowerCase())) {
      this.setError(this.dataset.errorDuplicate);
      return;
    }

    this.submitCodes([...applied, code], code);
  }

  handleClick(event) {
    const removeButton = event.target.closest('[data-discount-remove]');
    if (!removeButton || this.classList.contains('is-loading')) return;

    const { discountRemove: code } = removeButton.dataset;
    const remaining = this.appliedCodes.filter((existing) => existing.toLowerCase() !== code.toLowerCase());
    this.submitCodes(remaining);
  }

  async submitCodes(codes, addedCode = null) {
    this.clearError();
    this.classList.add('is-loading');

    try {
      const cart = await this.updateDiscounts(codes);

      if (addedCode && !discountCodesOn(cart).includes(addedCode.toLowerCase())) {
        await this.updateDiscounts(codes.filter((code) => code !== addedCode));
        this.setError(this.dataset.errorInvalid.replace('[code]', addedCode));
        return;
      }

      this.renderCart(cart);

      /*
       * Published after the re-render so subscribers act on the markup now on
       * screen. Only a code the shopper actually added is announced -- removals
       * and codes the cart rejected have already returned above.
       */
      if (addedCode) {
        document.dispatchEvent(
          new CustomEvent('theme:discount:applied', {
            detail: { code: addedCode, cart },
            bubbles: true,
          })
        );
      }
    } catch (error) {
      console.error('cart-discount: could not update cart discounts', error);
      this.setError(this.dataset.errorGeneric);
    } finally {
      this.classList.remove('is-loading');
    }
  }

  async updateDiscounts(codes) {
    const body = JSON.stringify({
      discount: codes.join(','),
      sections: CART_DISCOUNT_SECTIONS.map((section) => section.section),
      sections_url: window.location.pathname,
    });

    const response = await fetch(routes.cart_update_url, { ...fetchConfig(), body });
    if (!response.ok) throw new Error(`Cart discount update failed: ${response.status}`);

    return response.json();
  }

  /*
   * Swaps in the server-rendered markup so every price stays Liquid-formatted.
   * This element lives inside .drawer__inner and is replaced along with it, so
   * nothing may touch `this` afterwards.
   */
  renderCart(cart) {
    for (const { id, section, selector } of CART_DISCOUNT_SECTIONS) {
      const container = document.getElementById(id);
      const html = cart.sections?.[section];
      if (!container || !html) continue;

      const target = container.querySelector(selector) ?? container;
      const source = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
      if (source) target.innerHTML = source.innerHTML;
    }

    /*
     * The control that had focus was just replaced. Applying a code swaps the
     * field out for the applied badge, so fall back through what the new markup
     * actually offers to keep focus inside the drawer's focus trap.
     */
    const nextFocus =
      document.getElementById('CartDrawer-DiscountCode') ??
      document.querySelector('.cart-drawer__discount-remove') ??
      document.getElementById('CartDrawer-Checkout');
    nextFocus?.focus();
  }

  setError(message) {
    this.classList.toggle('cart-drawer__discount--error', Boolean(message));
    if (this.errorElement) this.errorElement.textContent = message ?? '';
  }

  clearError() {
    this.setError('');
  }
}

/*
 * Every manually entered code on the cart, lowercased. Codes can land at cart
 * level or on individual lines depending on what the discount targets, and
 * automatic discounts sit alongside them — those are skipped, since the shopper
 * never typed them and cannot remove them.
 */
function discountCodesOn(cart) {
  const codes = [];

  const collect = (applications) => {
    for (const entry of applications ?? []) {
      const application = entry.discount_application ?? entry;
      if (application?.type === 'discount_code' && application.title) {
        codes.push(application.title.toLowerCase());
      }
    }
  };

  collect(cart.cart_level_discount_applications);
  for (const item of cart.items ?? []) collect(item.line_level_discount_allocations);

  return codes;
}

if (!customElements.get('cart-discount')) {
  customElements.define('cart-discount', CartDiscount);
}
